import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Chart from '../components/Chart';
import MetricCard from '../components/MetricCard';
import * as FiIcons from 'react-icons/fi';

const { FiSmile, FiMeh, FiFrown, FiTrendingUp, FiFilter, FiDownload } = FiIcons;

const SentimentAnalysis = () => {
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const sentimentMetrics = [
    {
      title: 'Positive Sentiment',
      value: '78%',
      change: '5%',
      changeType: 'positive',
      icon: FiSmile,
      color: 'success',
      subtitle: '+3% from last week'
    },
    {
      title: 'Neutral Sentiment',
      value: '16%',
      change: '2%',
      changeType: 'negative',
      icon: FiMeh,
      color: 'warning',
      subtitle: 'Stable range'
    },
    {
      title: 'Negative Sentiment',
      value: '6%',
      change: '3%',
      changeType: 'negative',
      icon: FiFrown,
      color: 'danger',
      subtitle: 'Needs attention'
    },
    {
      title: 'Sentiment Score',
      value: '8.2/10',
      change: '0.5',
      changeType: 'positive',
      icon: FiTrendingUp,
      color: 'primary',
      subtitle: 'Overall health'
    }
  ];

  const emotionData = {
    labels: ['Joy', 'Trust', 'Surprise', 'Sadness', 'Anger', 'Fear', 'Disgust', 'Anticipation'],
    series: [
      {
        name: 'Emotion Distribution',
        type: 'radar',
        data: [
          {
            value: [85, 78, 45, 12, 8, 15, 5, 65],
            name: 'Current Week',
            itemStyle: { color: '#0ea5e9' },
            areaStyle: { color: 'rgba(14, 165, 233, 0.2)' }
          },
          {
            value: [75, 70, 38, 18, 15, 20, 8, 55],
            name: 'Previous Week',
            itemStyle: { color: '#64748b' },
            areaStyle: { color: 'rgba(100, 116, 139, 0.1)' }
          }
        ]
      }
    ]
  };

  const categoryBreakdown = {
    labels: ['Product Quality', 'Customer Service', 'Delivery', 'Pricing', 'User Experience'],
    series: [
      {
        name: 'Positive',
        type: 'bar',
        stack: 'sentiment',
        data: [85, 78, 92, 65, 88],
        itemStyle: { color: '#22c55e' }
      },
      {
        name: 'Neutral',
        type: 'bar',
        stack: 'sentiment',
        data: [10, 15, 6, 25, 8],
        itemStyle: { color: '#f59e0b' }
      },
      {
        name: 'Negative',
        type: 'bar',
        stack: 'sentiment',
        data: [5, 7, 2, 10, 4],
        itemStyle: { color: '#ef4444' }
      }
    ]
  };

  const keywordData = [
    { word: 'excellent', count: 245, sentiment: 'positive' },
    { word: 'fast', count: 198, sentiment: 'positive' },
    { word: 'helpful', count: 187, sentiment: 'positive' },
    { word: 'slow', count: 156, sentiment: 'negative' },
    { word: 'amazing', count: 134, sentiment: 'positive' },
    { word: 'disappointed', count: 89, sentiment: 'negative' },
    { word: 'confusing', count: 67, sentiment: 'negative' },
    { word: 'love', count: 234, sentiment: 'positive' }
  ];

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Sentiment Analysis</h1>
          <p className="text-secondary-600 mt-1">Deep dive into customer emotions and feedback patterns</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <select 
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium flex items-center space-x-2"
          >
            <FiDownload className="w-4 h-4" />
            <span>Export Report</span>
          </motion.button>
        </div>
      </div>

      {/* Sentiment Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {sentimentMetrics.map((metric, index) => (
          <MetricCard key={index} {...metric} />
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Chart 
          title="Emotion Analysis Radar"
          data={emotionData}
          type="radar"
          height={400}
          options={{
            radar: {
              indicator: emotionData.labels.map(label => ({ name: label, max: 100 })),
              shape: 'polygon',
              splitNumber: 5,
              axisName: {
                color: '#64748b'
              },
              splitLine: {
                lineStyle: {
                  color: '#e2e8f0'
                }
              },
              splitArea: {
                areaStyle: {
                  color: ['rgba(14, 165, 233, 0.05)', 'rgba(14, 165, 233, 0.1)']
                }
              }
            }
          }}
        />
        
        <Chart 
          title="Sentiment by Category"
          data={categoryBreakdown}
          type="bar"
          height={400}
          options={{
            yAxis: {
              type: 'value',
              max: 100,
              axisLabel: {
                formatter: '{value}%'
              }
            }
          }}
        />
      </div>

      {/* Keyword Analysis */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-secondary-900">Top Keywords</h3>
          <div className="flex items-center space-x-2">
            <FiFilter className="w-4 h-4 text-secondary-500" />
            <select 
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-1 border border-secondary-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Categories</option>
              <option value="product">Product</option>
              <option value="service">Service</option>
              <option value="delivery">Delivery</option>
            </select>
          </div>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {keywordData.map((keyword, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1 }}
              className={`p-4 rounded-lg border-2 ${
                keyword.sentiment === 'positive' 
                  ? 'border-success-200 bg-success-50' 
                  : keyword.sentiment === 'negative'
                  ? 'border-danger-200 bg-danger-50'
                  : 'border-warning-200 bg-warning-50'
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-secondary-900">"{keyword.word}"</span>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  keyword.sentiment === 'positive' 
                    ? 'bg-success-200 text-success-800' 
                    : keyword.sentiment === 'negative'
                    ? 'bg-danger-200 text-danger-800'
                    : 'bg-warning-200 text-warning-800'
                }`}>
                  {keyword.sentiment}
                </span>
              </div>
              <p className="text-sm text-secondary-600">{keyword.count} mentions</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Recent Feedback Samples */}
      <div className="bg-white rounded-xl shadow-sm border border-secondary-200 p-6">
        <h3 className="text-lg font-semibold text-secondary-900 mb-6">Recent Feedback Samples</h3>
        
        <div className="space-y-4">
          {[
            {
              text: "The customer service team was absolutely amazing! They resolved my issue within minutes and were so friendly throughout the process.",
              sentiment: 'positive',
              score: 0.95,
              category: 'Customer Service',
              timestamp: '2 hours ago'
            },
            {
              text: "Product quality is good but the delivery took longer than expected. Would appreciate better communication about delays.",
              sentiment: 'neutral',
              score: 0.3,
              category: 'Delivery',
              timestamp: '4 hours ago'
            },
            {
              text: "Very disappointed with the recent update. The new interface is confusing and I can't find basic features anymore.",
              sentiment: 'negative',
              score: -0.8,
              category: 'User Experience',
              timestamp: '6 hours ago'
            }
          ].map((feedback, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="p-4 border border-secondary-200 rounded-lg hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className={`w-3 h-3 rounded-full ${
                    feedback.sentiment === 'positive' 
                      ? 'bg-success-500' 
                      : feedback.sentiment === 'negative'
                      ? 'bg-danger-500'
                      : 'bg-warning-500'
                  }`}></div>
                  <span className="text-sm font-medium text-secondary-900">{feedback.category}</span>
                  <span className="text-xs text-secondary-500">{feedback.timestamp}</span>
                </div>
                <div className="text-sm font-medium text-secondary-700">
                  Score: {feedback.score}
                </div>
              </div>
              <p className="text-secondary-700 text-sm leading-relaxed">{feedback.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SentimentAnalysis;